import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { useSystemDate } from '../hooks/useSystemDate';
import { DateInput } from '../components/DateInput';
import { Plus, FileText, CheckCircle2, XCircle, Clock, Printer, FileDown, Copy, Trash2, Upload, Users, Search } from 'lucide-react';

const fmt = (n: any) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso: string) => {
  if (!iso) return '';
  const p = iso.split('-');
  return p.length === 3 ? `${p[2].padStart(2, '0')}-${p[1].padStart(2, '0')}-${p[0]}` : iso;
};

const parseAmt = (v: any) => parseFloat(String(v || '').replace(/,/g, '')) || 0;

const emptyItem = () => ({ name: '', hsn: '', qty: '', rate: '', amount: '' });

const Field = ({ label, value, onChange, placeholder, className, disabled, ...rest }: any) => (
  <div className={`flex flex-col ${className || ''}`}>
    <label className="text-[10px] text-gray-500 font-semibold mb-[1px] uppercase">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e: any) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="outline-none text-[13px] font-medium bg-white border border-gray-200 rounded px-2 py-[4px] focus:border-[#1e8585] focus:bg-[#f0fafa] transition-colors"
      spellCheck={false}
      {...rest}
    />
  </div>
);

// ─── Party Side Panel ────────────────────────────────────────────────────────
const PartyPanel = ({ onSelect }: { onSelect: (party: { name: string; address: string }) => void }) => {
  const activeCompany = useStore(s => s.activeCompany);
  const [search, setSearch] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(-1);

  const parties = useLiveQuery(async () => {
    if (!activeCompany) return [];
    const quots = await (db as any).quotations.where('companyId').equals(activeCompany.id!).toArray();
    const partyMap = new Map<string, { name: string; address: string; count: number; lastDate: string }>();
    quots.forEach((q: any) => {
      if (!q.partyName?.trim()) return;
      const key = q.partyName.trim().toLowerCase();
      const existing = partyMap.get(key);
      if (existing) {
        existing.count++;
        if (q.date > existing.lastDate) existing.lastDate = q.date;
      } else {
        partyMap.set(key, { name: q.partyName.trim(), address: q.partyAddress || '', count: 1, lastDate: q.date || '' });
      }
    });
    return Array.from(partyMap.values()).sort((a, b) => b.lastDate.localeCompare(a.lastDate));
  }, [activeCompany]);

  const filtered = useMemo(() => {
    if (!parties) return [];
    if (!search.trim()) return parties;
    const q = search.toLowerCase();
    return parties.filter(p => p.name.toLowerCase().includes(q));
  }, [parties, search]);

  return (
    <div className="w-[220px] bg-white border-r border-gray-200 flex flex-col shrink-0">
      <div className="px-3 py-2 border-b border-gray-200 bg-[#f8fafc]">
        <div className="flex items-center gap-1.5 text-[11px] font-bold text-[#1e8585] uppercase tracking-wide mb-2">
          <Users className="w-3.5 h-3.5" /> Parties
        </div>
        <div className="relative">
          <Search className="w-3 h-3 absolute left-2 top-[7px] text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search party..."
            className="w-full border border-gray-200 rounded pl-6 pr-2 py-1 text-[11px] outline-none focus:border-[#1e8585]" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {(!filtered || filtered.length === 0) ? (
          <div className="p-3 text-center text-[10px] text-gray-400">
            {search ? 'No matching parties' : 'No parties yet — create a quotation first'}
          </div>
        ) : (
          filtered.map((party, idx) => (
            <div key={party.name} onClick={() => { setSelectedIdx(idx); onSelect(party); }}
              className={`px-3 py-2 border-b border-gray-50 cursor-pointer transition-colors ${
                selectedIdx === idx ? 'bg-[#1e8585] text-white' : 'hover:bg-[#f0f9f9]'
              }`}>
              <div className={`text-[12px] font-semibold truncate ${selectedIdx === idx ? 'text-white' : 'text-gray-800'}`}>
                {party.name}
              </div>
              <div className={`text-[10px] truncate ${selectedIdx === idx ? 'text-white/70' : 'text-gray-400'}`}>
                {party.address || 'No address'}
              </div>
              <div className={`text-[9px] mt-0.5 ${selectedIdx === idx ? 'text-white/50' : 'text-gray-300'}`}>
                {party.count} quotation{party.count > 1 ? 's' : ''}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ─── QuotationView ──────────────────────────────────────────────────────────
export const QuotationView = ({ handleItemSelect, quotationId, isAlterMode }: any) => {
  const activeCompany = useStore(s => s.activeCompany);
  const systemDate = useSystemDate();
  const setGlobalMessage = useStore(s => s.setGlobalMessage);

  const nextQuotNo = useLiveQuery(async () => {
    if (!activeCompany) return 1;
    const all = await (db as any).quotations.where('companyId').equals(activeCompany.id!).toArray();
    if (all.length === 0) return 1;
    return Math.max(...all.map((q: any) => parseInt(q.quotationNumber) || 0)) + 1;
  }, [activeCompany]);

  // ── Form state ──────────────────────────────────────────────────────────
  const [date, setDate] = useState(systemDate || '');
  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyGstin, setCompanyGstin] = useState('');
  const [companyState, setCompanyState] = useState('');
  const [companyStateCode, setCompanyStateCode] = useState('');
  const [companyMobile, setCompanyMobile] = useState('');
  const [partyName, setPartyName] = useState('');
  const [partyAddress, setPartyAddress] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [cgstRate, setCgstRate] = useState('');
  const [sgstRate, setSgstRate] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('Thanks for business with us.');
  const [bankName, setBankName] = useState('');
  const [bankAccountNo, setBankAccountNo] = useState('');
  const [bankIfsc, setBankIfsc] = useState('');
  const [signatureImage, setSignatureImage] = useState('');
  const [narration, setNarration] = useState('');
  const [existingQuot, setExistingQuot] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const inputRefs = useRef<Record<string, any>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Prefill company details from activeCompany ──────────────────────────
  useEffect(() => {
    if (!activeCompany || isAlterMode) return;
    setCompanyName(activeCompany.name || '');
    setCompanyAddress((activeCompany as any).address || '');
    setCompanyGstin((activeCompany as any).gstin || '');
    setCompanyState((activeCompany as any).state || '');
    setCompanyStateCode('');
    setCompanyMobile((activeCompany as any).mobile || (activeCompany as any).phone || '');
    setBankName((activeCompany as any).bankName || '');
    setBankAccountNo((activeCompany as any).bankAcct || '');
    setBankIfsc((activeCompany as any).bankIfsc || '');
  }, [activeCompany, isAlterMode]);

  // ── Totals ──────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    const sub = items.reduce((s, it) => s + parseAmt(it.amount), 0);
    const cRate = parseAmt(cgstRate);
    const sRate = parseAmt(sgstRate);
    const cgstAmt = sub * cRate / 100;
    const sgstAmt = sub * sRate / 100;
    const totalQty = items.reduce((s, it) => s + parseAmt(it.qty), 0);
    return { sub, cgstAmt, sgstAmt, grand: sub + cgstAmt + sgstAmt, totalQty };
  }, [items, cgstRate, sgstRate]);

  // ── Load alter mode ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAlterMode || !quotationId) return;
    const load = async () => {
      const q = await (db as any).quotations.get(Number(quotationId));
      if (!q) return;
      setExistingQuot(q);
      setDate(q.date || '');
      setCompanyName(q.companyName || '');
      setCompanyAddress(q.companyAddress || '');
      setCompanyGstin(q.companyGstin || '');
      setCompanyState(q.companyState || '');
      setCompanyStateCode(q.companyStateCode || '');
      setCompanyMobile(q.companyMobile || '');
      setPartyName(q.partyName || '');
      setPartyAddress(q.partyAddress || '');
      const loadedItems = (q.items || []).map((it: any) => ({
        name: it.name || '', hsn: it.hsn || '',
        qty: String(it.qty || ''), rate: String(it.rate || ''), amount: String(it.amount || ''),
      }));
      setItems([...loadedItems, emptyItem()]);
      setCgstRate(q.cgstRate ? String(q.cgstRate) : '');
      setSgstRate(q.sgstRate ? String(q.sgstRate) : '');
      setTermsAndConditions(q.termsAndConditions || '');
      setBankName(q.bankName || '');
      setBankAccountNo(q.bankAccountNo || '');
      setBankIfsc(q.bankIfsc || '');
      setSignatureImage(q.signatureImage || '');
      setNarration(q.narration || '');
    };
    load();
  }, [isAlterMode, quotationId]);

  // ── Item handlers ───────────────────────────────────────────────────────
  const handleItemChange = (idx: number, field: string, val: string) => {
    setItems(prev => {
      const next = prev.map((it, i) => {
        if (i !== idx) return it;
        const upd: any = { ...it, [field]: val };
        if (field === 'qty' || field === 'rate') {
          const q = parseAmt(field === 'qty' ? val : it.qty);
          const r = parseAmt(field === 'rate' ? val : it.rate);
          upd.amount = q && r ? (q * r).toFixed(2) : upd.amount;
        }
        return upd;
      });
      const last = next[next.length - 1];
      if (last.name.trim()) return [...next, emptyItem()];
      return next;
    });
  };

  const removeItem = (idx: number) => {
    setItems(prev => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== idx);
      return next;
    });
  };

  // ── Signature upload ────────────────────────────────────────────────────
  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500000) {
      setGlobalMessage('Signature image 500KB se chhota hona chahiye');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSignatureImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  // ── Tab navigation ──────────────────────────────────────────────────────
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (isSaving) {
      if (e.key === 'y' || e.key === 'Y' || e.key === 'Enter') { e.preventDefault(); doSave(); }
      else if (e.key === 'n' || e.key === 'N' || e.key === 'Escape') { e.preventDefault(); setIsSaving(false); }
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'a') { e.preventDefault(); setIsSaving(true); }
    if (e.key === 'Escape') handleItemSelect({ name: 'Quit' });
  }, [isSaving]);

  // ── Save ────────────────────────────────────────────────────────────────
  const doSave = async () => {
    if (!activeCompany) return;
    if (!companyName.trim()) { setGlobalMessage('Company name fill karo'); setIsSaving(false); return; }
    if (!partyName.trim()) { setGlobalMessage('Party name fill karo'); setIsSaving(false); return; }
    const validItems = items.filter(it => it.name.trim());
    if (validItems.length === 0) { setGlobalMessage('Kam se kam ek item daalo'); setIsSaving(false); return; }

    setSaving(true);
    try {
      const mappedItems = validItems.map(it => ({
        name: it.name.trim(), hsn: it.hsn.trim(),
        qty: parseAmt(it.qty), rate: parseAmt(it.rate), amount: parseAmt(it.amount),
      }));

      const quotData: any = {
        companyId: activeCompany.id!,
        date,
        companyName: companyName.trim(),
        companyAddress: companyAddress.trim(),
        companyGstin: companyGstin.trim(),
        companyState: companyState.trim(),
        companyStateCode: companyStateCode.trim(),
        companyMobile: companyMobile.trim(),
        partyName: partyName.trim(),
        partyAddress: partyAddress.trim(),
        items: mappedItems,
        cgstRate: parseAmt(cgstRate),
        cgstAmount: totals.cgstAmt,
        sgstRate: parseAmt(sgstRate),
        sgstAmount: totals.sgstAmt,
        subtotal: totals.sub,
        totalAmount: totals.grand,
        termsAndConditions: termsAndConditions.trim(),
        bankName: bankName.trim(),
        bankAccountNo: bankAccountNo.trim(),
        bankIfsc: bankIfsc.trim(),
        signatureImage,
        narration: narration.trim(),
        status: 'open',
        convertedToVoucherId: null,
      };

      if (isAlterMode && existingQuot) {
        await (db as any).quotations.update(existingQuot.id, { ...quotData, quotationNumber: existingQuot.quotationNumber });
        setGlobalMessage(`Quotation #${existingQuot.quotationNumber} updated`);
      } else {
        const qNum = nextQuotNo || 1;
        quotData.quotationNumber = String(qNum);
        await (db as any).quotations.add(quotData);
        setGlobalMessage(`Quotation #${qNum} saved`);
      }
      setIsSaving(false);
      handleItemSelect({ name: 'Quit' });
    } catch (e: any) {
      setGlobalMessage(`Error: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const isConverted = existingQuot?.status === 'converted';
  const quotNo = isAlterMode ? existingQuot?.quotationNumber || '' : String(nextQuotNo || '...');

  return (
    <div
      className="h-full w-full flex flex-col font-sans select-none overflow-hidden bg-[#f5f7fa] text-black relative"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {/* ── Accept dialog ── */}
      {isSaving && (
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white border-2 border-[#1e8585] w-[280px] shadow-2xl rounded-lg flex flex-col items-center py-5 px-4">
            <div className="text-[14px] text-black mb-1 font-bold">{isAlterMode ? 'Update' : 'Save'} Quotation</div>
            <div className="text-[12px] text-[#1e8585] mb-1 font-semibold">No. {quotNo}</div>
            <div className="text-[12px] text-gray-500 mb-3">Total: ₹ {fmt(totals.grand)}</div>
            <div className="text-[16px] font-bold mb-4">Accept?</div>
            <div className="flex space-x-6 text-[13px] font-semibold">
              <button className="px-4 py-1 bg-[#1e8585] text-white rounded hover:bg-[#166e6e]" onClick={doSave}><u>Y</u>es</button>
              <button className="px-4 py-1 bg-gray-200 rounded hover:bg-gray-300" onClick={() => setIsSaving(false)}><u>N</u>o</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Top bar ── */}
      <div className="bg-[#1e8585] text-white flex justify-between items-center px-3 py-[6px] text-[13px] font-bold shrink-0 shadow-sm">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Quotation {isAlterMode ? 'Alteration' : 'Creation'}
        </div>
        <div className="text-[12px] font-medium opacity-90">{activeCompany?.name}</div>
        <div className="cursor-pointer font-bold px-2 py-[2px] hover:bg-white/20 rounded" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</div>
      </div>

      {/* ── Main layout: Side Panel + Form ── */}
      <div className="flex-1 flex overflow-hidden">
        {/* Party Side Panel */}
        {!isAlterMode && <PartyPanel onSelect={(party: any) => {
          setPartyName(party.name || '');
          setPartyAddress(party.address || '');
        }} />}

        {/* Form */}
        <div className="flex-1 overflow-y-auto">
        <div className="max-w-[900px] mx-auto p-4 space-y-4">

          {/* Quotation No + Date */}
          <div className="flex items-center gap-4 bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-gray-500 font-semibold">Quotation No:</span>
              <span className="text-[15px] font-bold text-[#1e8585]">{quotNo}</span>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-[12px] text-gray-500 font-semibold">Date:</span>
              <DateInput
                value={date}
                onChange={(v: string) => { if (v) setDate(v); }}
                onConfirm={() => {}}
                autoFocus={!isAlterMode}
                className="w-[110px] font-bold text-[13px] text-right outline-none px-2 py-[3px] border border-gray-200 rounded bg-white focus:border-[#1e8585]"
              />
            </div>
            {isConverted && (
              <div className="flex items-center gap-1 bg-emerald-100 text-emerald-700 px-3 py-1 text-[11px] font-semibold rounded-full">
                <CheckCircle2 className="w-3.5 h-3.5" /> Converted
              </div>
            )}
          </div>

          {/* Company Details */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm border-l-[3px] border-l-[#1e8585]">
            <div className="text-[11px] font-bold text-[#1e8585] uppercase mb-3 tracking-wide flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#1e8585] text-white text-[10px] flex items-center justify-center font-bold">1</span>
              Company Details
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Company Name" value={companyName} onChange={setCompanyName} placeholder="Company name" className="col-span-2" disabled={isConverted} />
              <Field label="Address" value={companyAddress} onChange={setCompanyAddress} placeholder="Full address" className="col-span-2" disabled={isConverted} />
              <Field label="GSTIN" value={companyGstin} onChange={setCompanyGstin} placeholder="GSTIN number" disabled={isConverted} />
              <Field label="Mobile" value={companyMobile} onChange={setCompanyMobile} placeholder="Mobile number" disabled={isConverted} />
              <Field label="State" value={companyState} onChange={setCompanyState} placeholder="State name" disabled={isConverted} />
              <Field label="State Code" value={companyStateCode} onChange={setCompanyStateCode} placeholder="e.g. 09" disabled={isConverted} />
            </div>
          </div>

          {/* Bill To / Party */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm border-l-[3px] border-l-amber-500">
            <div className="text-[11px] font-bold text-amber-600 uppercase mb-3 tracking-wide flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center font-bold">2</span>
              Bill To
            </div>
            <div className="grid grid-cols-1 gap-3">
              <Field label="Party Name" value={partyName} onChange={setPartyName} placeholder="Customer / Party name" disabled={isConverted} />
              <Field label="Address" value={partyAddress} onChange={setPartyAddress} placeholder="Customer address" disabled={isConverted} />
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm border-l-[3px] border-l-blue-500">
            <div className="text-[11px] font-bold text-blue-600 uppercase px-4 py-2 tracking-wide border-b border-gray-100 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center font-bold">3</span>
              Items
            </div>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase">
                  <th className="text-left px-3 py-2 w-[30px]">#</th>
                  <th className="text-left px-2 py-2">Item Name</th>
                  <th className="text-left px-2 py-2 w-[100px]">HSN/SAC</th>
                  <th className="text-right px-2 py-2 w-[70px]">Qty</th>
                  <th className="text-right px-2 py-2 w-[110px]">Rate/Unit</th>
                  <th className="text-right px-2 py-2 w-[120px]">Amount</th>
                  <th className="w-[30px]"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50/50">
                    <td className="px-3 py-[3px] text-gray-400 text-[12px]">{item.name.trim() ? idx + 1 : ''}</td>
                    <td className="px-1 py-[2px]">
                      <input
                        type="text" value={item.name}
                        onChange={e => handleItemChange(idx, 'name', e.target.value)}
                        placeholder={idx === items.length - 1 ? 'Type item name...' : ''}
                        disabled={isConverted}
                        className="w-full outline-none px-1 py-[3px] bg-transparent focus:bg-[#f0fafa] rounded text-[13px]"
                        spellCheck={false}
                      />
                    </td>
                    <td className="px-1 py-[2px]">
                      <input
                        type="text" value={item.hsn}
                        onChange={e => handleItemChange(idx, 'hsn', e.target.value)}
                        disabled={isConverted || !item.name.trim()}
                        className="w-full outline-none px-1 py-[3px] bg-transparent focus:bg-[#f0fafa] rounded text-[13px] text-center"
                        spellCheck={false}
                      />
                    </td>
                    <td className="px-1 py-[2px]">
                      <input
                        type="text" value={item.qty}
                        onChange={e => handleItemChange(idx, 'qty', e.target.value)}
                        disabled={isConverted || !item.name.trim()}
                        className="w-full outline-none px-1 py-[3px] bg-transparent focus:bg-[#f0fafa] rounded text-[13px] text-right"
                      />
                    </td>
                    <td className="px-1 py-[2px]">
                      <input
                        type="text" value={item.rate}
                        onChange={e => handleItemChange(idx, 'rate', e.target.value)}
                        disabled={isConverted || !item.name.trim()}
                        className="w-full outline-none px-1 py-[3px] bg-transparent focus:bg-[#f0fafa] rounded text-[13px] text-right"
                      />
                    </td>
                    <td className="px-1 py-[2px]">
                      <input
                        type="text" value={item.amount}
                        onChange={e => handleItemChange(idx, 'amount', e.target.value)}
                        disabled={isConverted || !item.name.trim()}
                        className="w-full outline-none px-1 py-[3px] bg-transparent focus:bg-[#f0fafa] rounded text-[13px] text-right font-semibold"
                      />
                    </td>
                    <td className="px-1 py-[2px] text-center">
                      {item.name.trim() && items.length > 1 && !isConverted && (
                        <button onClick={() => removeItem(idx)} className="text-gray-300 hover:text-red-400 p-[2px]">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals section */}
            <div className="border-t-2 border-gray-200">
              <div className="flex justify-between items-center px-4 py-2 text-[13px] font-bold bg-gray-50">
                <span>Total</span>
                <div className="flex items-center gap-6">
                  <span className="text-gray-500 text-[12px]">Qty: {totals.totalQty}</span>
                  <span>₹ {fmt(totals.sub)}</span>
                </div>
              </div>
              {/* CGST / SGST */}
              <div className="flex items-center justify-end gap-3 px-4 py-2 border-t border-gray-100 text-[13px]">
                <span className="text-gray-500">CGST @</span>
                <input
                  type="text" value={cgstRate}
                  onChange={e => setCgstRate(e.target.value)}
                  disabled={isConverted}
                  placeholder="0"
                  className="w-[50px] outline-none border border-gray-200 rounded px-2 py-[2px] text-right text-[13px] focus:border-[#1e8585]"
                />
                <span className="text-gray-500">%</span>
                <span className="w-[120px] text-right font-semibold">{fmt(totals.cgstAmt)}</span>
              </div>
              <div className="flex items-center justify-end gap-3 px-4 py-2 border-t border-gray-100 text-[13px]">
                <span className="text-gray-500">SGST @</span>
                <input
                  type="text" value={sgstRate}
                  onChange={e => setSgstRate(e.target.value)}
                  disabled={isConverted}
                  placeholder="0"
                  className="w-[50px] outline-none border border-gray-200 rounded px-2 py-[2px] text-right text-[13px] focus:border-[#1e8585]"
                />
                <span className="text-gray-500">%</span>
                <span className="w-[120px] text-right font-semibold">{fmt(totals.sgstAmt)}</span>
              </div>
              {/* Grand total */}
              <div className="flex justify-between items-center px-4 py-3 border-t-2 border-gray-300 bg-gray-50 text-[15px] font-bold">
                <span>Grand Total</span>
                <span className="text-[#1e8585]">₹ {fmt(totals.grand)}</span>
              </div>
            </div>
          </div>

          {/* Terms, Bank, Signature */}
          <div className="grid grid-cols-2 gap-4">
            {/* Left: Terms + Bank */}
            <div className="space-y-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm border-l-[3px] border-l-purple-500">
                <div className="text-[11px] font-bold text-purple-600 uppercase mb-2 tracking-wide flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-500 text-white text-[10px] flex items-center justify-center font-bold">4</span>
                  Terms & Conditions
                </div>
                <textarea
                  value={termsAndConditions}
                  onChange={e => setTermsAndConditions(e.target.value)}
                  disabled={isConverted}
                  rows={3}
                  className="w-full outline-none text-[13px] border border-gray-200 rounded px-2 py-1 resize-none focus:border-[#1e8585] bg-white"
                  spellCheck={false}
                />
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm border-l-[3px] border-l-emerald-500">
                <div className="text-[11px] font-bold text-emerald-600 uppercase mb-2 tracking-wide flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] flex items-center justify-center font-bold">5</span>
                  Bank Details
                </div>
                <div className="space-y-2">
                  <Field label="Bank Name" value={bankName} onChange={setBankName} placeholder="Bank name" disabled={isConverted} />
                  <Field label="Account No." value={bankAccountNo} onChange={setBankAccountNo} placeholder="Account number" disabled={isConverted} />
                  <Field label="IFSC Code" value={bankIfsc} onChange={setBankIfsc} placeholder="IFSC code" disabled={isConverted} />
                </div>
              </div>
            </div>

            {/* Right: Signature + Narration */}
            <div className="space-y-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm border-l-[3px] border-l-rose-400">
                <div className="text-[11px] font-bold text-rose-500 uppercase mb-2 tracking-wide flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-rose-400 text-white text-[10px] flex items-center justify-center font-bold">6</span>
                  Signature
                </div>
                {signatureImage ? (
                  <div className="relative bg-gray-50 border border-dashed border-gray-200 rounded-lg p-3 flex items-center justify-center">
                    <img src={signatureImage} alt="Signature" className="max-h-[120px] max-w-full object-contain" />
                    {!isConverted && (
                      <button
                        onClick={() => setSignatureImage('')}
                        className="absolute top-2 right-2 text-red-400 hover:text-red-600 bg-white rounded-full p-1 shadow-md border border-gray-200"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isConverted}
                    className="flex items-center gap-2 text-[12px] text-gray-500 border border-dashed border-gray-300 rounded px-3 py-2 hover:border-[#1e8585] hover:text-[#1e8585] transition-colors"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    Upload Signature Image
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleSignatureUpload}
                />
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm border-l-[3px] border-l-slate-400">
                <div className="text-[11px] font-bold text-slate-500 uppercase mb-2 tracking-wide flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-slate-400 text-white text-[10px] flex items-center justify-center font-bold">7</span>
                  Narration / Notes
                </div>
                <textarea
                  value={narration}
                  onChange={e => setNarration(e.target.value)}
                  disabled={isConverted}
                  rows={3}
                  placeholder="Any additional notes..."
                  className="w-full outline-none text-[13px] border border-gray-200 rounded px-2 py-1 resize-none focus:border-[#1e8585] bg-white"
                  spellCheck={false}
                />
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3 pb-4">
            {!isConverted && (
              <button
                onClick={() => setIsSaving(true)}
                disabled={saving}
                className="flex items-center gap-2 bg-[#1e8585] text-white text-[13px] font-bold px-5 py-[8px] rounded-lg transition-colors hover:bg-[#166e6e] disabled:opacity-50 shadow-sm"
              >
                <CheckCircle2 className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save (Ctrl+A)'}
              </button>
            )}
            {isAlterMode && existingQuot && (
              <>
                <button
                  onClick={() => useStore.getState().setPrintRequested(true)}
                  className="flex items-center gap-2 border border-gray-300 bg-white text-[13px] font-medium text-gray-700 px-4 py-[8px] rounded-lg hover:bg-gray-50 shadow-sm"
                >
                  <Printer className="w-4 h-4" /> Print
                </button>
                <button
                  onClick={() => useStore.getState().setExportRequested('pdf')}
                  className="flex items-center gap-2 border border-gray-300 bg-white text-[13px] font-medium text-gray-700 px-4 py-[8px] rounded-lg hover:bg-gray-50 shadow-sm"
                >
                  <FileDown className="w-4 h-4" /> Save as PDF
                </button>
              </>
            )}
            <button
              onClick={() => handleItemSelect({ name: 'Quit' })}
              className="flex items-center gap-2 border border-gray-300 bg-white text-[13px] font-medium text-gray-700 px-4 py-[8px] rounded-lg hover:bg-gray-50 shadow-sm ml-auto"
            >
              <XCircle className="w-4 h-4" /> Cancel
            </button>
          </div>

        </div>
      </div>
      </div>{/* close flex layout */}
    </div>
  );
};

// ─── QuotationListView ──────────────────────────────────────────────────────
export const QuotationListView = ({ handleItemSelect }: any) => {
  const activeCompany = useStore(s => s.activeCompany);
  const fromDate = useStore(s => s.fromDate);
  const toDate = useStore(s => s.toDate);
  const setGlobalMessage = useStore(s => s.setGlobalMessage);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const quotations = useLiveQuery(async () => {
    if (!activeCompany) return [];
    return await (db as any).quotations
      .where('companyId').equals(activeCompany.id!)
      .filter((q: any) => q.date >= fromDate && q.date <= toDate)
      .toArray();
  }, [activeCompany, fromDate, toDate]);

  const sorted = useMemo(() =>
    [...(quotations || [])].sort((a: any, b: any) => b.date.localeCompare(a.date)), [quotations]);

  const filtered = useMemo(() => {
    let result = sorted;
    if (statusFilter !== 'all') {
      result = result.filter((item: any) => item.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((item: any) =>
        (item.partyName || '').toLowerCase().includes(q) ||
        (item.quotationNumber || '').toLowerCase().includes(q) ||
        (item.narration || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [sorted, searchQuery, statusFilter]);

  const totalAmount = filtered.reduce((s: number, q: any) => s + (q.totalAmount || 0), 0);
  const openCount = filtered.filter((q: any) => q.status === 'open').length;
  const convertedCount = filtered.filter((q: any) => q.status === 'converted').length;
  const fmt2 = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtD = (iso: string) => { if (!iso) return ''; const [y, m, d] = iso.split('-'); return `${d}-${m}-${y}`; };

  const handleDuplicate = async (q: any) => {
    if (!activeCompany) return;
    try {
      const all = await (db as any).quotations.where('companyId').equals(activeCompany.id!).toArray();
      const nextNum = all.length === 0 ? 1 : Math.max(...all.map((x: any) => parseInt(x.quotationNumber) || 0)) + 1;
      const { id, quotationNumber, status, convertedToVoucherId, ...rest } = q;
      await (db as any).quotations.add({
        ...rest,
        quotationNumber: String(nextNum),
        date: new Date().toISOString().split('T')[0],
        status: 'open',
        convertedToVoucherId: null,
      });
      setGlobalMessage(`Quotation duplicated as #${nextNum}`);
    } catch (e: any) {
      setGlobalMessage(`Error: ${e.message}`);
    }
  };

  const [confirmDelete, setConfirmDelete] = useState<any>(null);

  const openQuotation = (q: any) => {
    useStore.getState().setCurrentQuotationId(q.id);
    handleItemSelect({ name: 'Alter Quotation' });
  };

  const updateStatus = async (q: any, newStatus: string) => {
    try {
      await (db as any).quotations.update(q.id, { status: newStatus });
      setGlobalMessage(`Quotation #${q.quotationNumber} marked as ${newStatus}`);
    } catch (e: any) {
      setGlobalMessage(`Error: ${e.message}`);
    }
  };

  const deleteQuotation = async (q: any) => {
    try {
      await (db as any).quotations.delete(q.id);
      setGlobalMessage(`Quotation #${q.quotationNumber} deleted`);
      setConfirmDelete(null);
    } catch (e: any) {
      setGlobalMessage(`Error: ${e.message}`);
    }
  };

  const statusBadge = (s: string) => {
    if (s === 'converted') return (
      <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
        <CheckCircle2 className="w-3 h-3" /> Converted
      </span>
    );
    if (s === 'accepted') return (
      <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
        <CheckCircle2 className="w-3 h-3" /> Accepted
      </span>
    );
    if (s === 'rejected') return (
      <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
        <XCircle className="w-3 h-3" /> Rejected
      </span>
    );
    if (s === 'cancelled') return (
      <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-200">
        <XCircle className="w-3 h-3" /> Cancelled
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[10px] font-bold bg-[#f0f9f9] text-[#1e8585] border border-[#cce8e8]">
        <Clock className="w-3 h-3" /> Open
      </span>
    );
  };

  const statusLabel = (s: string) => {
    if (s === 'converted') return 'Converted';
    if (s === 'accepted') return 'Accepted';
    if (s === 'rejected') return 'Rejected';
    if (s === 'cancelled') return 'Cancelled';
    return 'Open';
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowSearch(true);
        setTimeout(() => searchRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') {
        if (showSearch) { setShowSearch(false); setSearchQuery(''); }
        else handleItemSelect({ name: 'Quit' });
      }
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, filtered.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
      if (e.key === 'Enter' && selectedIdx >= 0 && selectedIdx < filtered.length) {
        e.preventDefault(); openQuotation(filtered[selectedIdx]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearch, selectedIdx, filtered]);

  return (
    <div className="flex flex-col w-full h-full bg-white absolute inset-0 font-sans text-[12px] text-black select-none">
      {/* Top Bar — DayBook style */}
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10 shrink-0">
        <span className="w-1/3 text-left">Quotation Register</span>
        <span className="w-1/3 text-center">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      {/* Info Bar — Date range + stats + actions */}
      <div className="bg-white text-black flex items-center justify-between px-2 py-1 font-bold border-b border-[#aebec8] z-10 shadow-sm shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-slate-500">{fmtD(fromDate)} to {fmtD(toDate)}</span>
          <div className="h-3 w-[1px] bg-slate-300" />
          {[
            { key: 'all', label: 'All', count: sorted.length, color: 'text-slate-600', active: 'bg-slate-600' },
            { key: 'open', label: 'Open', count: sorted.filter((q: any) => q.status === 'open').length, color: 'text-[#1e8585]', active: 'bg-[#1e8585]' },
            { key: 'accepted', label: 'Accepted', count: sorted.filter((q: any) => q.status === 'accepted').length, color: 'text-blue-600', active: 'bg-blue-600' },
            { key: 'converted', label: 'Converted', count: sorted.filter((q: any) => q.status === 'converted').length, color: 'text-emerald-600', active: 'bg-emerald-600' },
            { key: 'rejected', label: 'Rejected', count: sorted.filter((q: any) => q.status === 'rejected').length, color: 'text-amber-600', active: 'bg-amber-600' },
          ].map(f => (
            <button key={f.key} onClick={() => { setStatusFilter(f.key); setSelectedIdx(-1); }}
              className={`text-[10px] font-bold px-1.5 py-[1px] rounded transition-colors ${
                statusFilter === f.key ? `${f.active} text-white` : `${f.color} hover:opacity-70`
              }`}>
              {f.count} {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowSearch(!showSearch); if (!showSearch) setTimeout(() => searchRef.current?.focus(), 50); else setSearchQuery(''); }}
            className="text-[10px] text-slate-500 hover:text-[#1e8585] px-1.5 py-[2px] rounded border border-slate-200 hover:border-[#1e8585] transition-colors"
            title="Search (Ctrl+F)"
          >
            Ctrl+F
          </button>
          <button
            onClick={() => handleItemSelect({ name: 'Create Quotation' })}
            className="flex items-center gap-1 bg-[#1e8585] text-white text-[10px] px-2.5 py-[3px] rounded font-bold hover:bg-[#166e6e] transition-colors"
          >
            <Plus className="w-3 h-3" /> New
          </button>
        </div>
      </div>

      {/* Search Bar — Ctrl+F */}
      {showSearch && (
        <div className="flex items-center gap-2 px-2 py-1.5 bg-amber-50 border-b border-amber-200 shrink-0">
          <span className="text-[10px] text-amber-700 font-bold">FIND:</span>
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setSelectedIdx(0); }}
            placeholder="Search by party name, number, or narration..."
            className="flex-1 outline-none text-[12px] bg-white border border-amber-300 rounded px-2 py-[3px] focus:border-[#1e8585] focus:ring-1 focus:ring-[#1e8585]/20"
            spellCheck={false}
          />
          <span className="text-[10px] text-amber-600">{filtered.length} found</span>
          <button onClick={() => { setShowSearch(false); setSearchQuery(''); }} className="text-amber-500 hover:text-amber-700 text-[11px] font-bold px-1">✕</button>
        </div>
      )}

      {/* Column Headers */}
      <div className="flex w-full border-b border-[#aebec8] bg-white text-black font-bold px-2 py-1 shadow-sm shrink-0">
        <div className="w-[70px]">No.</div>
        <div className="w-[85px]">Date</div>
        <div className="flex-1">Party Name</div>
        <div className="w-[80px] text-center">Items</div>
        <div className="w-[120px] text-right">Amount</div>
        <div className="w-[90px] text-center">Status</div>
        <div className="w-[90px] text-center">Actions</div>
      </div>

      {/* Delete Confirmation */}
      {confirmDelete && (
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white border-2 border-rose-400 w-[300px] shadow-2xl rounded-lg flex flex-col items-center py-5 px-4">
            <Trash2 className="w-6 h-6 text-rose-500 mb-2" />
            <div className="text-[13px] text-black font-bold mb-1">Delete Quotation #{confirmDelete.quotationNumber}?</div>
            <div className="text-[11px] text-slate-500 mb-1">{confirmDelete.partyName}</div>
            <div className="text-[11px] text-slate-400 mb-4">This action cannot be undone.</div>
            <div className="flex gap-4 text-[12px] font-semibold">
              <button className="px-4 py-1.5 bg-rose-500 text-white rounded hover:bg-rose-600" onClick={() => deleteQuotation(confirmDelete)}>Delete</button>
              <button className="px-4 py-1.5 bg-gray-200 rounded hover:bg-gray-300" onClick={() => setConfirmDelete(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      <div ref={listRef} className="flex-1 overflow-y-auto bg-white scrollbar-thin pb-6">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center text-slate-400 py-16 gap-2">
            <FileText className="w-8 h-8 text-slate-300" />
            <span className="text-[12px]">{searchQuery ? 'No matching quotations found' : 'No quotations for this period'}</span>
            <span className="text-[10px] text-slate-300">Press N or click "New" to create one</span>
          </div>
        ) : (
          filtered.map((q: any, idx: number) => (
            <div
              key={q.id}
              className={`flex w-full px-2 py-[3px] cursor-pointer border-b border-slate-50 transition-colors ${
                selectedIdx === idx ? 'bg-[#1e8585] text-white font-bold' : 'text-black hover:bg-[#f0f9f9]'
              }`}
              onClick={() => openQuotation(q)}
              onMouseEnter={() => setSelectedIdx(idx)}
            >
              <div className={`w-[70px] font-semibold ${selectedIdx === idx ? 'text-white' : 'text-[#1e8585]'}`}>
                #{q.quotationNumber}
              </div>
              <div className={`w-[85px] ${selectedIdx === idx ? 'text-white/80' : 'text-slate-500'}`}>
                {fmtD(q.date)}
              </div>
              <div className="flex-1 truncate font-medium">
                {q.partyName || '—'}
              </div>
              <div className={`w-[80px] text-center ${selectedIdx === idx ? 'text-white/70' : 'text-slate-400'}`}>
                {(q.items || []).length}
              </div>
              <div className="w-[120px] text-right font-semibold">
                ₹{fmt2(q.totalAmount || 0)}
              </div>
              <div className="w-[90px] text-center">
                {selectedIdx === idx ? (
                  <span className="text-[10px] font-bold text-white/90">{statusLabel(q.status)}</span>
                ) : statusBadge(q.status)}
              </div>
              <div className="w-[90px] flex items-center justify-center gap-[2px]" onClick={e => e.stopPropagation()}>
                {q.status === 'open' && (
                  <button onClick={() => updateStatus(q, 'accepted')}
                    className={`p-[3px] rounded ${selectedIdx === idx ? 'text-white/60 hover:text-white' : 'text-gray-300 hover:text-blue-600 hover:bg-blue-50'}`}
                    title="Mark Accepted">
                    <CheckCircle2 className="w-3 h-3" />
                  </button>
                )}
                {(q.status === 'open' || q.status === 'accepted') && (
                  <button onClick={() => updateStatus(q, 'rejected')}
                    className={`p-[3px] rounded ${selectedIdx === idx ? 'text-white/60 hover:text-white' : 'text-gray-300 hover:text-amber-600 hover:bg-amber-50'}`}
                    title="Mark Rejected">
                    <XCircle className="w-3 h-3" />
                  </button>
                )}
                {(q.status === 'rejected' || q.status === 'cancelled') && (
                  <button onClick={() => updateStatus(q, 'open')}
                    className={`p-[3px] rounded ${selectedIdx === idx ? 'text-white/60 hover:text-white' : 'text-gray-300 hover:text-[#1e8585] hover:bg-[#f0f9f9]'}`}
                    title="Reopen">
                    <Clock className="w-3 h-3" />
                  </button>
                )}
                <button onClick={() => handleDuplicate(q)}
                  className={`p-[3px] rounded ${selectedIdx === idx ? 'text-white/60 hover:text-white' : 'text-gray-300 hover:text-[#1e8585] hover:bg-gray-100'}`}
                  title="Duplicate">
                  <Copy className="w-3 h-3" />
                </button>
                {q.status !== 'converted' && (
                  <button onClick={() => setConfirmDelete(q)}
                    className={`p-[3px] rounded ${selectedIdx === idx ? 'text-white/60 hover:text-white' : 'text-gray-300 hover:text-rose-500 hover:bg-rose-50'}`}
                    title="Delete">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer — Total bar */}
      <div className="border-t-2 border-[#aebec8] flex w-full bg-white text-black font-bold px-2 py-1 shrink-0">
        <div className="w-[70px]">{filtered.length}</div>
        <div className="w-[85px] text-slate-500 font-normal text-[11px]">Quotation(s)</div>
        <div className="flex-1"></div>
        <div className="w-[80px]"></div>
        <div className="w-[120px] text-right text-[#1e8585]">₹{fmt2(totalAmount)}</div>
        <div className="w-[90px]"></div>
        <div className="w-[90px]"></div>
      </div>
    </div>
  );
};
